import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { companyInfo, faqs } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Quote Request Sent!",
        description: "Thank you for contacting us. We'll get back to you during business hours (Mon-Fri 9AM-5PM).",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        serviceType: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error sending your request. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to discuss your commercial refrigeration needs? Our team is here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Phone,
                title: "Office",
                content: "772-362-9384",
                link: "tel:772-362-9384",
                subtitle: "Devon Granda - Executive Administrator"
              },
              {
                icon: Phone,
                title: "Owner/Manager",
                content: "772-353-7263",
                link: "tel:772-353-7263",
                subtitle: "Luis Granda",
                highlight: true
              },
              {
                icon: Mail,
                title: "Email",
                content: "devon.granda@subzerocoolers.net",
                link: "mailto:devon.granda@subzerocoolers.net"
              },
              {
                icon: Clock,
                title: "Business Hours",
                content: "Mon-Fri: 9AM-5PM",
                subContent: "Serving Florida Statewide"
              }
            ].map((item, index) => (
              <Card 
                key={index} 
                className={`border-gray-800 hover:border-orange-500/50 transition-all duration-300 ${
                  item.highlight ? 'bg-gradient-to-br from-orange-500/10 to-orange-600/10' : 'bg-gray-900'
                }`}
              >
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-500/10 mb-4">
                    <item.icon className="text-orange-500" size={24} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">{item.title}</h3>
                  {item.subtitle && (
                    <p className="text-xs text-gray-500 mb-2">{item.subtitle}</p>
                  )}
                  {item.link ? (
                    <a 
                      href={item.link} 
                      className="text-white font-semibold hover:text-orange-500 transition-colors block"
                    >
                      {item.content}
                    </a>
                  ) : (
                    <>
                      <p className="text-white font-semibold">{item.content}</p>
                      {item.subContent && (
                        <p className="text-orange-500 text-sm mt-1">{item.subContent}</p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Request a Quote
              </h2>
              <p className="text-gray-400 mb-8">
                Fill out the form below and our team will get back to you promptly during business hours (Mon-Fri 9AM-5PM).
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-300">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-300">Company</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serviceType" className="text-gray-300">Service Type *</Label>
                  <Select
                    value={formData.serviceType}
                    onValueChange={(value) => setFormData({ ...formData, serviceType: value })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white focus:border-orange-500">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="installation">Installation</SelectItem>
                      <SelectItem value="repair">Repair & Maintenance</SelectItem>
                      <SelectItem value="custom">Custom Design</SelectItem>
                      <SelectItem value="emergency">Emergency Service</SelectItem>
                      <SelectItem value="consultation">Consultation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-300">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-gray-800 border-gray-700 text-white focus:border-orange-500"
                    placeholder="Tell us about your refrigeration needs..."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold"
                >
                  {isSubmitting ? (
                    <>Sending...</>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Company Info */}
            <div className="space-y-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0">
                      <MapPin className="text-orange-500" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Service Area</h3>
                      <p className="text-gray-400">Serving commercial clients across the state of Florida with expert refrigeration services.</p>
                    </div>
                  </div>
                  
                  <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop" 
                      alt="Florida Service Area" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-orange-500">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-black mb-3">
                    Need to Speak with Us?
                  </h3>
                  <p className="text-black/80 mb-4">
                    <strong>Devon Granda</strong> - Executive Administrator<br/>
                    Office: 772-362-9384<br/>
                    devon.granda@subzerocoolers.net
                  </p>
                  <p className="text-black/80 mb-6">
                    <strong>Luis Granda</strong> - Owner/Manager<br/>
                    Cell: 772-353-7263<br/>
                    subzerocoolers@outlook.com
                  </p>
                  <Button 
                    asChild
                    size="lg"
                    className="bg-black hover:bg-gray-900 text-orange-500 font-bold"
                  >
                    <a href="tel:772-362-9384">
                      <Phone className="mr-2" size={20} />
                      Call Office
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400 text-lg">
              Find answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-gray-900 border-gray-800 rounded-lg px-6"
              >
                <AccordionTrigger className="text-white hover:text-orange-500 text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Contact;
